import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Lock, PenLine, Sparkles, X } from 'lucide-react';
import type { OutreachConfig, OutreachRecord, OutreachTemplate } from '@/types/outreach';
import { STARTER_OUTREACH_TEMPLATES } from '@/data/outreachStarterTemplates';
import { subscribeToOutreachTemplates } from '@/services/outreach';
import { getOrCreateConversation, sendMessage } from '@/services/dm';
import {
  composeHello,
  fillTemplate,
  firstNameOf,
  joinedLabel,
  personalLineState,
  providerLabel,
} from '@/utils/outreach';
import { MemberAvatar } from './MemberAvatar';

type TemplateChoice = Pick<OutreachTemplate, 'id' | 'title' | 'body' | 'language'> & {
  suggestWhenInviter?: boolean;
};

interface Leader {
  uid: string;
  displayName: string;
}

interface SayHelloSheetProps {
  record: OutreachRecord;
  leader: Leader;
  config: OutreachConfig;
  /** Next member waiting after this one (shown after sending). */
  next: OutreachRecord | null;
  waitingAfter: number;
  /** Close without sending: the claim is released by the parent. */
  onCancel: () => void;
  onDone: () => void;
  onSayHelloToNext: (record: OutreachRecord) => void;
  onOpenConversation: (conversationId: string) => void;
}

const PRIMARY_BUTTON = 'text-white bg-gradient-to-r from-[#8e4585] to-[#63345e]';

/** Templates from /admin; the built-in starters until some have been created. */
function useTemplates(): TemplateChoice[] | null {
  const [templates, setTemplates] = useState<TemplateChoice[] | null>(null);
  useEffect(() => {
    return subscribeToOutreachTemplates((items) => {
      const active = items.filter((t) => t.isActive);
      setTemplates(
        active.length > 0 ? active : STARTER_OUTREACH_TEMPLATES.filter((t) => t.isActive)
      );
    });
  }, []);
  return templates;
}

function pickInitialTemplate(
  templates: TemplateChoice[],
  record: OutreachRecord,
  leaderId: string
): TemplateChoice {
  const iInvited = record.invitedBy === leaderId;
  return (iInvited && templates.find((t) => t.suggestWhenInviter)) || templates[0];
}

function errorMessage(e: unknown): string {
  const encryption = e instanceof Error && e.message.includes('Encryption');
  return encryption
    ? 'Messaging is still getting ready. Please try again in a moment.'
    : "That didn't send. Please try again.";
}

export function SayHelloSheet(props: SayHelloSheetProps) {
  const { record, leader, onCancel, onDone } = props;
  const [sent, setSent] = useState<{ text: string; conversationId: string } | null>(null);
  const first = firstNameOf(record.displayName);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={sent ? onDone : onCancel}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={sent ? `${first} has been welcomed` : `Say hello to ${record.displayName}`}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]"
      >
        <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-slate-200 sm:hidden" />
        {sent ? (
          <SentPanel {...props} first={first} leaderName={leader.displayName} sent={sent} />
        ) : (
          <ComposePanel {...props} first={first} onSent={setSent} />
        )}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------

function SentPanel({
  record,
  first,
  leaderName,
  sent,
  next,
  waitingAfter,
  onDone,
  onSayHelloToNext,
  onOpenConversation,
}: SayHelloSheetProps & {
  first: string;
  leaderName: string;
  sent: { text: string; conversationId: string };
}) {
  const now = useMemo(() => Date.now(), []);
  return (
    <div className="p-5 text-center">
      <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
      <h3 className="mt-2 text-lg font-semibold text-slate-800 font-['Merriweather',serif]">
        {first} has been welcomed
      </h3>
      <p className="text-sm text-slate-500">Thank you, {leaderName}. Your hello is on its way.</p>
      <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-left text-sm text-slate-700 whitespace-pre-wrap line-clamp-4">
        {sent.text}
      </div>
      <p className="mt-1 text-xs text-slate-400">Delivered to {record.displayName}</p>

      {next && (
        <div className="mt-4 text-left">
          <h4 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">
            Next up · {waitingAfter} more waiting
          </h4>
          <div className="mt-2 flex items-center gap-3">
            <MemberAvatar
              memberId={next.memberId}
              name={next.displayName}
              photoURL={next.photoURL}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800 truncate">{next.displayName}</p>
              <p className="text-xs text-slate-500">
                {joinedLabel(next.joinedAt?.toMillis?.() ?? now, now)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSayHelloToNext(next)}
            className={`mt-3 w-full py-3 rounded-full font-semibold ${PRIMARY_BUTTON}`}
          >
            Say hello to {firstNameOf(next.displayName)}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => onOpenConversation(sent.conversationId)}
        className="mt-2 w-full py-3 rounded-full border border-slate-200 text-slate-700 font-medium"
      >
        Open conversation with {first}
      </button>
      <button type="button" onClick={onDone} className="mt-1 w-full py-2 text-sm text-slate-500">
        Done for now
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ComposePanel({
  record,
  leader,
  config,
  first,
  onCancel,
  onSent,
}: SayHelloSheetProps & {
  first: string;
  onSent: (sent: { text: string; conversationId: string }) => void;
}) {
  const templates = useTemplates();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [personal, setPersonal] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = useMemo(() => Date.now(), []);

  const vars = useMemo(
    () => ({ firstName: first, hebrewName: record.hebrewName, leaderName: leader.displayName }),
    [first, record.hebrewName, leader.displayName]
  );

  // Start from the first template (the inviter's one when I invited them).
  useEffect(() => {
    if (!templates || templateId !== null || templates.length === 0) return;
    const chosen = pickInitialTemplate(templates, record, leader.uid);
    setTemplateId(chosen.id);
    setBody(fillTemplate(chosen.body, vars));
  }, [templates, templateId, record, leader.uid, vars]);

  const state = personalLineState(personal, config.personalLine);
  const canSend = body.trim().length > 0 && state.ok && !sending;
  const message = composeHello(body, personal);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const conversationId = await getOrCreateConversation(leader.uid, record.memberId);
      await sendMessage(conversationId, leader.uid, { text: message }, leader.displayName);
      onSent({ text: message, conversationId });
    } catch (e) {
      console.error('Failed to send hello:', e);
      setError(errorMessage(e));
      setSending(false);
    }
  };

  const sendLabel = state.ok ? 'Send hello' : 'Add your line to send';

  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <MemberAvatar
          memberId={record.memberId}
          name={record.displayName}
          photoURL={record.photoURL}
          size={48}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h3 className="text-lg font-semibold text-slate-800 truncate font-['Merriweather',serif]">
              {record.displayName}
            </h3>
            {record.hebrewName && (
              <span className="text-slate-500 truncate" dir="rtl" lang="he">
                {record.hebrewName}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {[
              joinedLabel(record.joinedAt?.toMillis?.() ?? now, now),
              providerLabel(record.provider),
              record.invitedByName ? `Invited by ${record.invitedByName}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
        <PenLine size={12} />
        You&apos;re saying hello · others will wait
      </div>

      <p className="mt-4 mb-2 text-sm text-slate-600">Start from a template</p>
      <div className="flex flex-wrap gap-2">
        {(templates ?? []).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTemplateId(t.id);
              setBody(fillTemplate(t.body, vars));
            }}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              templateId === t.id
                ? 'bg-[#8e4585] text-white border-[#8e4585]'
                : 'bg-white text-slate-700 border-slate-200 hover:border-[#8e4585]'
            }`}
          >
            {t.title}
          </button>
        ))}
      </div>

      <div className="mt-3 rounded-2xl border border-[#d9a9d3] focus-within:border-[#8e4585] p-3">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          dir="auto"
          className="w-full resize-none text-[15px] leading-relaxed text-slate-800 outline-none"
          aria-label="Message"
        />
        <textarea
          value={personal}
          onChange={(e) => setPersonal(e.target.value)}
          rows={2}
          dir="auto"
          placeholder={`Add a sentence about why you're glad ${first} joined`}
          className="w-full resize-none text-[15px] leading-relaxed text-slate-800 placeholder:italic placeholder:text-[#c4a24e] outline-none border-t border-dashed border-[#c4a24e]/60 pt-2"
          aria-label="Your own line"
        />
        {config.personalLine.required && (
          <PersonalLineHint ok={state.ok} count={state.count} min={config.personalLine.minChars} />
        )}
      </div>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
        <Lock size={12} />
        Private and encrypted. Only you and {first} can read this.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-rose-600">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-full border border-slate-200 text-slate-700 font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={`flex-[1.4] py-3 rounded-full font-semibold transition ${
            canSend ? PRIMARY_BUTTON : 'bg-slate-100 text-slate-400'
          }`}
        >
          {sending ? 'Sending…' : sendLabel}
        </button>
      </div>
    </div>
  );
}

function PersonalLineHint({ ok, count, min }: { ok: boolean; count: number; min: number }) {
  return (
    <div className="mt-1 flex items-center justify-between text-xs">
      <span
        className={`inline-flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-slate-600'}`}
      >
        <Sparkles size={12} className={ok ? '' : 'text-[#c4a24e]'} />
        {ok ? 'Looks personal' : 'Add a line of your own to send'}
      </span>
      <span className="text-slate-400">
        {Math.min(count, min)} / {min}
      </span>
    </div>
  );
}
